import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Star, Plus, Edit, Power } from 'lucide-react';
import { starApi } from '../../lib/api';
import { toast } from 'sonner';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../components/ui/dialog';

export default function AdminStars() {
  const [stars, setStars] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingStar, setEditingStar] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    direct_referrals: 0,
    level2_refs: 0,
    level3_refs: 0,
    level4_refs: 0,
    level5_refs: 0,
    business_volume: 0,
    monthly_salary: 0
  });

  useEffect(() => {
    fetchStars();
  }, []);

  const fetchStars = async () => {
    try {
      const response = await starApi.getAll();
      setStars(response.data.stars);
    } catch (error) {
      console.error('Error fetching stars:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleOpenModal = (star = null) => {
    if (star) {
      setEditingStar(star);
      setFormData({
        name: star.name,
        direct_referrals: star.direct_referrals,
        level2_refs: star.level2_refs,
        level3_refs: star.level3_refs,
        level4_refs: star.level4_refs,
        level5_refs: star.level5_refs,
        business_volume: star.business_volume,
        monthly_salary: star.monthly_salary
      });
    } else {
      setEditingStar(null);
      setFormData({
        name: '',
        direct_referrals: 0,
        level2_refs: 0,
        level3_refs: 0,
        level4_refs: 0,
        level5_refs: 0,
        business_volume: 0,
        monthly_salary: 0
      });
    }
    setShowModal(true);
  };

  const handleSubmit = async () => {
    if (!formData.name) {
      toast.error('Please enter a star name');
      return;
    }

    setSubmitting(true);
    try {
      if (editingStar) {
        await starApi.update(editingStar.id, formData);
        toast.success('Star level updated');
      } else {
        await starApi.create(formData);
        toast.success('Star level created');
      }
      setShowModal(false);
      setEditingStar(null);
      setFormData({
        name: '',
        direct_referrals: 0,
        level2_refs: 0,
        level3_refs: 0,
        level4_refs: 0,
        level5_refs: 0,
        business_volume: 0,
        monthly_salary: 0
      });
      fetchStars();
    } catch (error) {
      toast.error(editingStar ? 'Failed to update star level' : 'Failed to create star level');
    } finally {
      setSubmitting(false);
    }
  };

  const handleToggle = async (starId) => {
    try {
      await starApi.toggle(starId);
      toast.success('Star level status updated');
      fetchStars();
    } catch (error) {
      toast.error('Failed to update star level');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-10 h-10 border-4 border-amber-500/20 border-t-amber-500 rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="admin-stars">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="p-3 rounded-xl bg-yellow-500/10">
            <Star className="w-6 h-6 text-yellow-400" />
          </div>
          <div>
            <h1 className="text-2xl text-white font-medium">Star Program</h1>
            <p className="text-sm text-zinc-500">Manage community star levels and salaries</p>
          </div>
        </div>

        <Button onClick={() => handleOpenModal()} className="gold-btn" data-testid="create-star-btn">
          <Plus className="w-4 h-4 mr-2" /> Add Star Level
        </Button>
      </div>

      {/* Stars Grid */}
      <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
        {stars.map((star, idx) => (
          <motion.div
            key={star.id}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: idx * 0.1 }}
            className={`glass-card p-6 ${star.is_active === false ? 'opacity-60' : ''}`}
          >
            <div className="flex items-start justify-between mb-4">
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 rounded-full bg-amber-500/20 flex items-center justify-center">
                  <Star className="w-6 h-6 text-amber-400" />
                </div>
                <div>
                  <h3 className="text-white font-medium">{star.name}</h3>
                  <p className="text-amber-400 text-lg font-bold">${star.monthly_salary}/mo</p>
                </div>
              </div>
              <span className={`badge ${star.is_active !== false ? 'badge-success' : 'badge-danger'}`}>
                {star.is_active !== false ? 'Active' : 'Inactive'}
              </span>
            </div>

            <div className="space-y-2 text-sm">
              <div className="flex justify-between">
                <span className="text-zinc-500">Direct Refs</span>
                <span className="text-white">{star.direct_referrals}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-zinc-500">Level 2</span>
                <span className="text-white">{star.level2_refs}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-zinc-500">Level 3</span>
                <span className="text-white">{star.level3_refs}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-zinc-500">Level 4</span>
                <span className="text-white">{star.level4_refs}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-zinc-500">Level 5</span>
                <span className="text-white">{star.level5_refs}</span>
              </div>
              <div className="flex justify-between pt-2 border-t border-white/10">
                <span className="text-zinc-500">Business Vol.</span>
                <span className="text-amber-400">${star.business_volume.toLocaleString()}</span>
              </div>
            </div>

            <div className="flex gap-2 mt-4 pt-4 border-t border-white/10">
              <Button 
                variant="outline" 
                size="sm"
                onClick={() => handleOpenModal(star)}
                className="flex-1 border-white/10"
              >
                <Edit className="w-3 h-3 mr-1" /> Edit
              </Button>
              <Button 
                variant="outline" 
                size="sm"
                onClick={() => handleToggle(star.id)}
                className={`flex-1 ${star.is_active !== false ? 'border-red-500/30 text-red-400' : 'border-green-500/30 text-green-400'}`}
              >
                <Power className="w-3 h-3 mr-1" /> {star.is_active !== false ? 'Disable' : 'Enable'}
              </Button>
            </div>
          </motion.div>
        ))}
      </div>

      {/* Create Modal */}
      <Dialog open={showModal} onOpenChange={setShowModal}>
        <DialogContent className="bg-zinc-900 border-white/10">
          <DialogHeader>
            <DialogTitle className="text-white">{editingStar ? 'Edit Star Level' : 'Create Star Level'}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 pt-4">
            <div>
              <label className="text-xs text-zinc-400 mb-2 block">Star Name</label>
              <Input
                value={formData.name}
                onChange={(e) => setFormData({...formData, name: e.target.value})}
                placeholder="e.g., Gold Star"
                className="bg-black/50 border-white/10"
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="text-xs text-zinc-400 mb-2 block">Direct Referrals</label>
                <Input
                  type="number"
                  value={formData.direct_referrals}
                  onChange={(e) => setFormData({...formData, direct_referrals: parseInt(e.target.value)})}
                  className="bg-black/50 border-white/10"
                />
              </div>
              <div>
                <label className="text-xs text-zinc-400 mb-2 block">Level 2 Refs</label>
                <Input
                  type="number"
                  value={formData.level2_refs}
                  onChange={(e) => setFormData({...formData, level2_refs: parseInt(e.target.value)})}
                  className="bg-black/50 border-white/10"
                />
              </div>
              <div>
                <label className="text-xs text-zinc-400 mb-2 block">Level 3 Refs</label>
                <Input
                  type="number"
                  value={formData.level3_refs}
                  onChange={(e) => setFormData({...formData, level3_refs: parseInt(e.target.value)})}
                  className="bg-black/50 border-white/10"
                />
              </div>
              <div>
                <label className="text-xs text-zinc-400 mb-2 block">Level 4 Refs</label>
                <Input
                  type="number"
                  value={formData.level4_refs}
                  onChange={(e) => setFormData({...formData, level4_refs: parseInt(e.target.value)})}
                  className="bg-black/50 border-white/10"
                />
              </div>
              <div>
                <label className="text-xs text-zinc-400 mb-2 block">Level 5 Refs</label>
                <Input
                  type="number"
                  value={formData.level5_refs}
                  onChange={(e) => setFormData({...formData, level5_refs: parseInt(e.target.value)})}
                  className="bg-black/50 border-white/10"
                />
              </div>
              <div>
                <label className="text-xs text-zinc-400 mb-2 block">Business Volume ($)</label>
                <Input
                  type="number"
                  value={formData.business_volume}
                  onChange={(e) => setFormData({...formData, business_volume: parseFloat(e.target.value)})}
                  className="bg-black/50 border-white/10"
                />
              </div>
            </div>

            <div>
              <label className="text-xs text-zinc-400 mb-2 block">Monthly Salary ($)</label>
              <Input
                type="number"
                value={formData.monthly_salary}
                onChange={(e) => setFormData({...formData, monthly_salary: parseFloat(e.target.value)})}
                className="bg-black/50 border-white/10"
              />
            </div>

            <Button onClick={handleSubmit} disabled={submitting} className="w-full gold-btn">
              {submitting ? (
                <div className="w-5 h-5 border-2 border-black/20 border-t-black rounded-full animate-spin" />
              ) : (
                editingStar ? 'Update Star Level' : 'Create Star Level'
              )}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
